import { useEffect, useRef, useState } from 'react';
import { useSlideFields } from '../lib/useSlideFields.ts';
import { hasNestedJsx } from '../lib/fieldChecks.ts';
import { accentLabel } from '../lib/format.ts';
import { optionBooleanHint, optionControlLabel, optionValueLabel } from '../lib/optionLabels.ts';
import AccentSwatches from './AccentSwatches.tsx';
import MediaPickerModal from '../components/MediaPickerModal.tsx';
import ListGroupSection from './ListEditor.tsx';
import { FieldControl, LockedField, MediaRow } from './FieldControls.tsx';
import { OptionRow, OptionRows, Segmented, Toggle } from '../components/Controls.tsx';
import { api } from '../lib/api.ts';
import { usePersistedState } from '../lib/persistedState.ts';
import { setBusySlide } from '../lib/userBusy.ts';
import { geometryAsk } from '../lib/geometryAsk.ts';
import { requestSlideChecks } from '../lib/slideChecks.ts';
import type { GeometryFindingKind } from '../lib/geometryAsk.ts';
import type {
  DeckOptions,
  DeckSection,
  MediaField,
  Scalar,
  SlideEntry,
  SlideLayout,
  SlideOption,
} from '../../shared/deck.ts';
import type { SlideWritePayload } from '../../shared/api.ts';
import type { ReactNode } from 'react';

/**
 * A geometry finding, as this panel holds it. `source` is the CHANNEL that
 * reported it (a clean report only clears a nudge from the same one, since
 * "no overlaps" says nothing about overflow) and `kind` is the finding — the
 * two diverge, because the layout channel reports both overlaps and crowding.
 */
type Nudge = { source: 'overflow' | 'layout'; kind: GeometryFindingKind; summary: string };

/** One visual option control, after `unifyOptions` folds every instance of an
 *  element+prop into one. */
type OptionGroup = {
  key: string;
  element: string;
  prop: string;
  /** The friendly name, before the multi-instance suffix. */
  baseLabel: string;
  label: string;
  type: SlideOption['type'];
  allowed?: string[];
  min?: number;
  max?: number;
  members: SlideOption[];
  /** null when the members disagree — no segment is lit until one is picked. */
  value: Scalar | null;
};

export type EditPanelProps = {
  slug: string;
  slide: SlideEntry;
  sections?: DeckSection[];
  onSlideEdited?: (res?: SlideWritePayload) => void;
  /** Prefills the composer — never sends. See lib/geometryAsk.ts. */
  onAskInChat?: (text: string) => void;
  onToggleHidden?: ((slide: SlideEntry) => void) | null;
  /** Returns null when the write failed (the mutation toasted it), which is
   *  what the optimistic switch reads to spring back. */
  onToggleLayoutOk?: ((slide: SlideEntry, next: boolean) => Promise<unknown>) | null;
  onToggleSectionChrome?: ((slide: SlideEntry, next: boolean) => Promise<unknown>) | null;
  /** Pin/release this slide's WORDS (manifest `copyLocked`). Like
   *  `onToggleSectionChrome` it is a decision ABOUT the slide rather than an
   *  edit to it, so it writes the manifest row and refreshes the summary. */
  onToggleCopyLocked?: ((slide: SlideEntry, next: boolean) => Promise<unknown>) | null;
  /** Undefined means NOT LOADED YET, not "no options" — the section-label row
   *  is hidden only when the deck-wide switch is known to be off. */
  deckOptions?: DeckOptions | null;
};

/**
 * The Edit tab: direct editing of the focused slide. Accent swatches on
 * top (dominant-token swap, server-side), then the slide's media (one row
 * per image/video, replace/add via the picker — upload or RKH bank), then
 * visual options, the slide's standalone text fields, and finally one
 * grouped LIST section per add/remove/reorder-able list (agenda sections,
 * cards, people, chart data points, table rows) — collapsible item cards
 * with drag-handle reordering instead of a flat wall of fields.
 * Saves happen on blur. Fields whose text carries styling spans are
 * read-only here (saving would flatten them); those go through chat.
 */
export default function EditPanel({
  slug,
  slide,
  sections,
  onSlideEdited,
  onAskInChat,
  onToggleHidden,
  onToggleLayoutOk,
  onToggleCopyLocked,
  onToggleSectionChrome,
  deckOptions,
}: EditPanelProps) {
  const sectionMeta = (sections || []).find((s) => s.key === slide.section);
  // Attribution clock for the geometry nudge: every successful panel write
  // (text, list op, option, accent, media, layout) stamps this ref, opening
  // a short window in which preview findings are blamed on the edit.
  const lastWriteRef = useRef(0);
  const {
    status,
    fields,
    lists,
    layout,
    accent,
    media,
    options,
    error,
    saving,
    conflict,
    reload,
    overwrite,
    discardPreview,
    saveField,
    setAccent,
    setMedia,
    setOption,
    applyListOp,
    setLayoutVariant,
  } = useSlideFields(slug, slide.id, {
    onSaved: (res) => {
      lastWriteRef.current = Date.now();
      onSlideEdited?.(res);
    },
  });
  // Author-approved layout. Mirrors the manifest flag off the slide summary;
  // held locally so the switch responds before the deck summary refreshes.
  const [layoutOk, setLayoutOk] = useState(!!slide.layoutOk);
  useEffect(() => {
    setLayoutOk(!!slide.layoutOk);
  }, [slide.id, slide.layoutOk]);
  // The author's word pin. Same mirror-the-manifest-flag pattern as layoutOk.
  const [copyLocked, setCopyLocked] = useState(!!slide.copyLocked);
  useEffect(() => {
    setCopyLocked(!!slide.copyLocked);
  }, [slide.id, slide.copyLocked]);
  // The top-right section label on THIS slide. Same mirror-the-manifest-flag
  // pattern; `slide.sectionChrome` is present only when suppressed.
  const [sectionChrome, setSectionChrome] = useState(slide.sectionChrome !== false);
  useEffect(() => {
    setSectionChrome(slide.sectionChrome !== false);
  }, [slide.id, slide.sectionChrome]);
  // Only meaningful when the deck shows the label at all: with the deck-wide
  // switch off there is nothing for one slide to opt back into, so the row is
  // hidden rather than shown doing nothing. `deckOptions` undefined = not
  // loaded yet, and the default is ON.
  const deckShowsSectionLabel = !deckOptions || deckOptions.sectionChrome !== false;
  // Media field awaiting a pick in the modal | null.
  const [pickingMedia, setPickingMedia] = useState<MediaField | null>(null);
  // Geometry nudge from the preview's self-checks | null.
  const [nudge, setNudge] = useState<Nudge | null>(null);
  const idxRef = useRef(slide.idx);
  useEffect(() => {
    idxRef.current = slide.idx;
  }, [slide.idx]);

  // Tell the rest of the editor the user's hands are on this slide, so a
  // background turn finishing can't rewrite it or navigate away mid-keystroke
  // (lib/userBusy.js). Saves are on blur, so an in-flight edit lives only in
  // the focused input — losing focus is exactly when it stops being fragile.
  // Cleared on unmount too: a remount is precisely the thing that destroys it.
  useEffect(() => () => setBusySlide(null), []);

  // The deck preview posts overflow/layout self-check results to this window
  // (same signals chat's ToolCard consumes during previews). Both are matched
  // to THIS slide by index — every mounted slide reports, so an unattributed
  // message would describe someone else's problem.
  //
  // These used to show only within 10s of a panel write, on the theory that a
  // pre-existing problem is not this edit's business. That left the common
  // case with no way through: the canvas showed a red badge, the panel said
  // nothing, and there was nothing to click (Jake, 2026-08-01). The finding is
  // now always shown for the focused slide, which is only fair because it now
  // comes with both exits — fix it in chat, or say it looks right.
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.origin !== window.location.origin) return;
      // Read defensively rather than against the sender's declared shape: the
      // message crosses a postMessage boundary, exactly as ToolCard's copy of
      // these two handlers does.
      const d = e.data;
      if (!d || typeof d !== 'object') return;
      if (typeof d.idx !== 'number' || d.idx !== idxRef.current) return;
      // A clean report only clears a nudge from the SAME check — the layout
      // check reporting "no overlaps" says nothing about overflow.
      if (d.type === 'rkh-slide-overflow') {
        if (!d.active) {
          setNudge((n) => (n?.source === 'overflow' ? null : n));
          return;
        }
        const detail = typeof d.detail === 'string' && d.detail ? ` (${d.detail.slice(0, 100)})` : '';
        setNudge({
          source: 'overflow',
          kind: 'overflow',
          summary: `Content runs past the frame${detail}`,
        });
      } else if (d.type === 'rkh-slide-layout') {
        const parts = [];
        for (const o of (d.overlaps || []).slice(0, 2)) {
          parts.push(`“${String(o.a).slice(0, 40)}” and “${String(o.b).slice(0, 40)}” overlap`);
        }
        for (const cr of (d.crowding || []).slice(0, 2)) {
          parts.push(
            cr.kind === 'edge'
              ? `“${String(cr.a).slice(0, 40)}” sits too close to the slide edge`
              : `“${String(cr.a).slice(0, 40)}” is jammed against “${String(cr.b).slice(0, 40)}”`,
          );
        }
        // `sparse` is deliberately ignored: switching to a roomier layout
        // legitimately lowers fill — nagging would fight the user's choice.
        if (parts.length === 0) {
          setNudge((n) => (n?.source === 'layout' ? null : n));
          return;
        }
        // The handoff asks for the fix the FINDING needs, so `kind` follows the
        // finding rather than the channel: an overlap wants room between two
        // elements, not the "tighten the copy so it fits" an overflow wants.
        setNudge({
          source: 'layout',
          kind: (d.overlaps || []).length > 0 ? 'overlap' : 'crowding',
          summary: parts.join(' · '),
        });
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // …and ASK, rather than waiting for a transition that already happened. The
  // panel is almost always opened AFTER the slide settled — most of all when it
  // was opened by clicking the red badge — so with no request there is nothing
  // to receive and the finding area sits empty next to a flagged slide
  // (Jake, 2026-08-02). Re-asked on every slide change: the answer is per-slide
  // and the previous slide's finding must not linger.
  useEffect(() => {
    setNudge(null);
    requestSlideChecks(slug);
  }, [slug, slide.id]);

  if (status === 'loading') {
    return (
      <div className="edit-panel">
        <p className="muted small">Loading slide…</p>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="edit-panel">
        <p className="error small">{error}</p>
        <button type="button" className="btn btn--ghost btn--sm" onClick={reload}>
          Retry
        </button>
      </div>
    );
  }

  const otherAccents = Object.keys(accent?.tokens || {}).filter((tk) => tk !== accent?.token);

  // Fields and media claimed by a list render inside its item cards; the
  // rest stay in the flat sections below.
  const fieldById = new Map(fields.map((f) => [f.id, f]));
  const mediaById = new Map(media.map((m) => [m.id, m]));
  const claimedFieldIds = new Set(lists.flatMap((g) => g.entries.flatMap((e) => e.fieldIds)));
  const claimedMediaIds = new Set(lists.flatMap((g) => g.entries.map((e) => e.mediaFieldId).filter(Boolean)));
  const flatFields = fields.filter((f) => !claimedFieldIds.has(f.id));
  const editableFields = flatFields.filter((f) => !hasNestedJsx(f.value));
  const lockedFields = flatFields.filter((f) => hasNestedJsx(f.value));
  const freeMedia = media.filter((m) => !claimedMediaIds.has(m.id));
  const allOptionGroups = unifyOptions(options);
  // Whole-slide switches are pulled out of Options into their own pinned group
  // at the top: they act on the slide itself (its chrome, whether it ships),
  // not on the content below. Keeping them here rather than in the filmstrip
  // kebab keeps that menu to structural verbs and puts appearance where every
  // other appearance control already lives.
  const logoGroup = allOptionGroups.find((g) => g.prop === 'logo') || null;
  const optionGroups = allOptionGroups.filter((g) => g !== logoGroup);
  // What a COLLAPSED Slide section is hiding. Only non-default state is worth
  // saying — an untouched slide reads as an empty header, which is the point:
  // the shut section still answers "has anything been waived here?" without
  // the user opening every slide to check.
  const slideSummary =
    [
      logoGroup?.value === false && 'Logo off',
      deckShowsSectionLabel && !sectionChrome && 'Section label off',
      layoutOk && 'Layout check off',
      copyLocked && 'Words locked',
      slide.hidden && 'Hidden',
    ]
      .filter(Boolean)
      .join(' · ') || null;

  return (
    <div
      className="edit-panel"
      // React's onFocus/onBlur are focusin/focusout, so they bubble from the
      // fields. Guarding on `contains(relatedTarget)` keeps the panel "busy"
      // while focus moves BETWEEN its own fields — only leaving it entirely
      // (or a click on the canvas, the filmstrip, Review…) releases the hold.
      onFocus={() => setBusySlide(slide.id)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setBusySlide(null);
      }}
    >
      <header style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', minWidth: 0 }}>
        <span className="muted small" style={{ flex: 'none' }}>
          {String((slide.idx ?? 0) + 1).padStart(2, '0')}
        </span>
        <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{slide.title}</strong>
        {sectionMeta && (
          <span
            className="small"
            style={{
              flex: 'none',
              padding: '0.05rem 0.5rem',
              borderRadius: 999,
              border: '1px solid var(--border, #444)',
              color: 'var(--muted)',
            }}
          >
            {sectionMeta.name || slide.section}
          </span>
        )}
        <span
          className="muted small"
          style={{ marginLeft: 'auto', flex: 'none', visibility: saving ? 'visible' : 'hidden' }}
          aria-live="polite"
        >
          Saving…
        </span>
      </header>

      {conflict && (
        <div className="edit-panel__conflict" role="alert">
          <p>{conflict.message}</p>
          <div className="edit-panel__conflict-actions">
            {conflict.code === 'preview' ? (
              // The file IS the chat proposal — reverting it (the card stays in
              // the thread) is the only way to unblock direct editing here.
              <button type="button" className="btn btn--primary btn--sm" onClick={discardPreview}>
                Discard preview &amp; edit here
              </button>
            ) : (
              <>
                <button type="button" className="btn btn--ghost btn--sm" onClick={reload}>
                  Reload fields
                </button>
                {conflict.retry && (
                  <button type="button" className="btn btn--primary btn--sm" onClick={overwrite}>
                    Keep my edit
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* A geometry warning, and both ways out of it. "Looks right" is the one
          that matters: the checks are only a proxy for how the slide reads,
          and the person looking at it is the better judge — so saying so
          silences it for good (the manifest flag), not just for this session.
          Dismiss is the lighter option: hide it here, keep the check on. */}
      {nudge && !conflict && !layoutOk && (
        <div className="edit-panel__nudge" role="status">
          <p>{nudge.summary}</p>
          <div className="edit-panel__conflict-actions">
            {onAskInChat && (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => {
                  onAskInChat(geometryAsk(nudge));
                  setNudge(null);
                }}
              >
                Fix in chat
              </button>
            )}
            {onToggleLayoutOk && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => {
                  setLayoutOk(true);
                  setNudge(null);
                  onToggleLayoutOk(slide, true);
                }}
                title="Turns this slide's Layout check off — no more warnings about how it fits"
              >
                Looks right
              </button>
            )}
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setNudge(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {(logoGroup || onToggleHidden || onToggleCopyLocked) && (
        <Group id="slide" title="Slide" defaultOpen={false} summary={slideSummary} rows>
          {logoGroup && <OptionControl group={logoGroup} disabled={saving} onSet={setOption} />}
          {/* ONE switch for every geometry check on this slide, not one per
              check: a second toggle would make the user diagnose which kind of
              warning they were looking at before they could silence it. */}
          <SlideSwitchRow
            label="Layout check"
            checked={!layoutOk}
            disabled={saving}
            onSet={async (next) => {
              // Optimistic, then through the editor so the SUMMARY refetches —
              // the switch reads its position off `slide.layoutOk` in the slide
              // list, so a write that only reloaded the preview sprang back on
              // as soon as the user left the slide and returned.
              setLayoutOk(!next);
              lastWriteRef.current = Date.now();
              if (onToggleLayoutOk) {
                const res = await onToggleLayoutOk(slide, !next);
                if (res === null) setLayoutOk(next); // mutate.run toasted the failure
                return;
              }
              await api.patch(`/api/slides/${slug}/slide/layout-ok?id=${encodeURIComponent(slide.id)}`, {
                layoutOk: !next,
              });
              onSlideEdited?.();
            }}
            hint={
              layoutOk
                ? 'Off: you accepted how this slide looks, so it stops warning about content running past the frame or elements overlapping. It is still listed in check-overflow.'
                : 'Warns when content runs past the frame or elements overlap. Turn it off for a slide you have looked at and are happy with.'
            }
          />
          {/* The words, not the look. Sits next to Layout check because both
              are decisions ABOUT the slide that the AI must respect, but they
              answer different questions: that one waives geometry warnings,
              this one binds what the AI may do to the copy. */}
          {onToggleCopyLocked && (
            <SlideSwitchRow
              label="Keep my exact words"
              checked={copyLocked}
              disabled={saving}
              onSet={async (next) => {
                setCopyLocked(next);
                lastWriteRef.current = Date.now();
                const res = await onToggleCopyLocked(slide, next);
                if (res === null) setCopyLocked(!next); // mutate.run toasted the failure
              }}
              hint={
                copyLocked
                  ? 'On: the AI reproduces this slide’s words exactly. To make it fit it shrinks the type or splits the slide, and it will tell you rather than trim. Layout, colour and imagery stay editable.'
                  : 'Off: the AI may tighten or shorten this slide’s copy to make it fit. Turn it on for approved or quoted text that must not change.'
              }
            />
          )}
          {onToggleSectionChrome && deckShowsSectionLabel && (
            <SlideSwitchRow
              label="Section label"
              checked={sectionChrome}
              disabled={saving}
              onSet={async (next) => {
                setSectionChrome(next);
                lastWriteRef.current = Date.now();
                const res = await onToggleSectionChrome(slide, next);
                if (res === null) setSectionChrome(!next); // mutate.run toasted the failure
              }}
              hint={
                sectionChrome
                  ? 'Shows this deck’s "Part One · Section name · 03/06" line, top right.'
                  : 'Hidden on this slide only. The rest of the deck keeps it.'
              }
            />
          )}
          {onToggleHidden && (
            <SlideSwitchRow
              label="Show when published"
              checked={!slide.hidden}
              disabled={saving}
              onSet={() => onToggleHidden(slide)}
              hint={
                slide.hidden ? 'Kept in the deck, left out when published.' : 'Ships with the deck when you publish.'
              }
            />
          )}
        </Group>
      )}

      <Group id="accent" title="Accent">
        {accent?.inherits ? (
          <AccentSwatches
            value={sectionMeta?.color || null}
            onPick={() => {}}
            disabled
            inheritedFrom={`the ${sectionMeta?.name || slide.section} section (recolour the section in the filmstrip)`}
          />
        ) : accent?.token ? (
          <>
            <AccentSwatches value={accent.token} onPick={(v) => setAccent(v)} disabled={saving} />
            {otherAccents.length > 0 && (
              <p className="muted small edit-panel__note">
                Swaps the slide's main accent ({accentLabel(accent.token)}); its{' '}
                {otherAccents.map((tk) => accentLabel(tk)).join(', ')} stays; use chat to change that.
              </p>
            )}
          </>
        ) : (
          <p className="muted small edit-panel__note">This slide has no accent colour.</p>
        )}
      </Group>

      {slide.multiIndex !== undefined && (
        <p className="muted small edit-panel__note">This slide repeats from one file: edits apply to every instance.</p>
      )}

      {(flatFields.length > 0 || (fields.length === 0 && lists.length === 0)) && (
        <Group id="text" title="Text" count={editableFields.length + lockedFields.length}>
          {fields.length === 0 && lists.length === 0 && (
            <p className="muted small edit-panel__note">
              No directly editable text on this slide: ask in chat instead.
            </p>
          )}
          {editableFields.map((f) => (
            // Saves are queued + serialised in useSlideFields, so typing stays
            // enabled while an earlier field's PUT is in flight.
            <FieldControl key={f.id} field={f} onSave={saveField} />
          ))}
          {lockedFields.map((f) => (
            <LockedField key={f.id} field={f} onAskInChat={onAskInChat} />
          ))}
        </Group>
      )}

      {lists.map((group) => (
        <ListGroupSection
          key={group.id}
          slug={slug}
          group={group}
          fieldById={fieldById}
          mediaById={mediaById}
          saving={saving}
          onSaveField={saveField}
          onListOp={applyListOp}
          onPickMedia={setPickingMedia}
          onAskInChat={onAskInChat}
        />
      ))}

      {freeMedia.length > 0 && (
        <Group id="media" title="Media" count={freeMedia.length}>
          {freeMedia.map((m) => (
            <MediaRow key={m.id} slug={slug} field={m} disabled={saving} onReplace={() => setPickingMedia(m)} />
          ))}
        </Group>
      )}

      {(layout || optionGroups.length > 0) && (
        <Group id="options" title="Options" defaultOpen={false} count={optionGroups.length + (layout ? 1 : 0)} rows>
          {layout && <LayoutControl layout={layout} disabled={saving} onPick={setLayoutVariant} />}
          {optionGroups.map((g) => (
            <OptionControl key={g.key} group={g} disabled={saving} onSet={setOption} />
          ))}
        </Group>
      )}

      {pickingMedia && (
        <MediaPickerModal
          kind={pickingMedia.kind}
          hint={pickingMedia.hint || null}
          slug={slug}
          label={`${pickingMedia.assetName ? 'Replace' : 'Add'}: ${pickingMedia.label}`}
          busy={saving}
          onCancel={() => setPickingMedia(null)}
          onPick={async (payload) => {
            await setMedia(pickingMedia.id, payload); // throws → modal shows the error
            setPickingMedia(null);
          }}
        />
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- options */

const ELEMENT_NOUNS: Record<string, string> = {
  Card: 'cards',
  NumItem: 'items',
  BigStat: 'stats',
  StatSlide: 'stats',
  ImageSlide: 'images',
  VideoSlide: 'videos',
  TeamGrid: 'grids',
};

/** Group options by element+prop. Repeated elements (3 Cards) collapse to
 *  ONE control — within a slide, items share their visual treatment, so a
 *  pick fans out to every instance. Mixed current values → no active
 *  segment until picked. */
function unifyOptions(options: SlideOption[]): OptionGroup[] {
  // `value`/`label` are set on the way out, so the map holds the group minus
  // the two fields that depend on every member being in.
  const groups = new Map<string, Omit<OptionGroup, 'value' | 'label'>>();
  for (const o of options) {
    const key = `${o.element}|${o.prop}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        element: o.element,
        prop: o.prop,
        // Named here rather than in OptionControl so the multi-instance
        // suffix below composes onto the friendly name ("Style — all cards").
        baseLabel: optionControlLabel(o.element, o.prop, o.label),
        type: o.type,
        allowed: o.allowed,
        min: o.min,
        max: o.max,
        members: [],
      };
      groups.set(key, g);
    }
    g.members.push(o);
  }
  return [...groups.values()].map((g) => {
    const values = g.members.map((m) => m.value);
    const first = values[0];
    const agreed = values.every((v) => v === first);
    const noun = ELEMENT_NOUNS[g.element] || `${g.element.toLowerCase()}s`;
    return {
      ...g,
      value: agreed && first !== undefined ? first : null,
      label: g.members.length > 1 ? `${g.baseLabel} (all ${noun})` : g.baseLabel,
    };
  });
}

/** The layout row: one segment per template layout preset (gallery
 *  variants projected onto the layout props). Nothing highlighted when the
 *  slide's current arrangement matches no preset (mixed/dynamic). */
function LayoutControl({
  layout,
  disabled,
  onPick,
}: {
  layout: SlideLayout;
  disabled?: boolean;
  /** Takes the CHOICE NAME, which is what `setLayoutVariant` sends as
   *  `{variant}` — not the choice object. */
  onPick: (name: string) => Promise<unknown>;
}) {
  const [busy, setBusy] = useState(false);
  async function pick(name: string) {
    if (busy || layout.current === name) return;
    setBusy(true);
    try {
      await onPick(name);
    } catch {
      /* conflict banner shows the message */
    } finally {
      setBusy(false);
    }
  }
  const many = wideForRow(layout.choices.map((c) => ({ label: c.name })));
  return (
    <OptionRow label="Layout" stack={many}>
      <Segmented
        value={layout.current}
        onChange={pick}
        disabled={disabled || busy}
        wrap={many}
        label="Layout"
        options={layout.choices.map((c) => ({
          value: c.name,
          label: c.name,
          title:
            Object.entries(c.props)
              .filter(([, v]) => v !== null)
              .map(([k, v]) => `${k}: ${v}`)
              .join(' · ') || 'template default',
        }))}
      />
    </OptionRow>
  );
}

/** A collapsible panel section. Open/closed is remembered per section across
 *  slides and reloads (localStorage) — the panel's shape is a working
 *  preference, not per-slide state. `count` labels a collapsed section so it
 *  is never a blind door. `rows` wraps the children in the shared options card
 *  (hairline-separated rows) — for sections that are settings rather than
 *  fields; text and media groups stay a plain stack of inputs. */
function Group({
  id,
  title,
  defaultOpen = true,
  count,
  summary,
  rows = false,
  children,
}: {
  id: string;
  title: string;
  defaultOpen?: boolean;
  count?: number;
  summary?: string | null;
  rows?: boolean;
  children?: ReactNode;
}) {
  const [open, setOpen] = usePersistedState(`studio.edit.group.${id}`, defaultOpen);
  return (
    <section className="edit-panel__group">
      <button
        type="button"
        className="edit-panel__toggle"
        aria-expanded={open ? 'true' : 'false'}
        onClick={() => setOpen((v) => !v)}
      >
        <svg className="edit-panel__toggle-caret" viewBox="0 0 10 6" width="10" height="6" aria-hidden="true">
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        {title}
        {!open && (summary || count != null) && (
          <span className={'edit-panel__toggle-count' + (summary ? ' edit-panel__toggle-count--state' : '')}>
            {summary || count}
          </span>
        )}
      </button>
      {open && (rows ? <OptionRows>{children}</OptionRows> : children)}
    </section>
  );
}

/** A switch for slide-level state that isn't a JSX prop (so it can't ride
 *  through OptionControl/setOption). The old two-segment `Approved | Checked`
 *  pill made the reader work out which word was the current state and which
 *  was the alternative; a switch plus a sentence says both. `hint` follows the
 *  state, so it always describes what IS, not what could be. */
function SlideSwitchRow({
  label,
  checked,
  hint,
  disabled,
  onSet,
}: {
  label: string;
  checked: boolean;
  /** Follows the STATE, so it always describes what is, not what could be. */
  hint?: string;
  disabled?: boolean;
  onSet: (next: boolean) => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const set = async (next: boolean) => {
    if (busy || next === checked) return;
    setBusy(true);
    try {
      await onSet(next);
    } finally {
      setBusy(false);
    }
  };
  return (
    <OptionRow label={label} hint={hint}>
      <Toggle checked={checked} onChange={set} label={label} disabled={disabled || busy} />
    </OptionRow>
  );
}

/** Does this segmented set need its own line? Roughly the width of the track:
 *  the segment text plus each segment's padding, against what's left beside a
 *  label in a ~360px panel. Cheap and stable — measuring would reflow on every
 *  slide change and the answer only has to be right, not exact. */
function wideForRow(options: { label: string | number }[]) {
  const chars = options.reduce((n, o) => n + String(o.label).length, 0);
  return chars * 6.6 + options.length * 22 > 210;
}

/** One visual option group: boolean → a switch; enum → one segment per value;
 *  number → stepper segments within min..max. Picking a value applies it to
 *  EVERY member instance (serialised PATCHes — each rolls the source hash).
 *  Segment text and the row label come from `optionLabels.js`; the VALUE sent
 *  to the codec is always the raw one. */
function OptionControl({
  group,
  disabled,
  onSet,
}: {
  group: OptionGroup;
  disabled?: boolean;
  /** Per OPTION id, not per group — one pick fans out to every member. */
  onSet: (id: string, value: Scalar) => Promise<unknown>;
}) {
  const [busy, setBusy] = useState(false);

  async function pick(val: Scalar) {
    if (busy || group.value === val) return;
    setBusy(true);
    try {
      for (const m of group.members) {
        if (m.value !== val) await onSet(m.id, val); // serial: rolling hash
      }
    } finally {
      setBusy(false);
    }
  }

  // A boolean was rendered as `Off | On` segments — two buttons to answer one
  // yes-or-no, and the reader had to spot which one was lit.
  if (group.type === 'boolean') {
    return (
      // `type` says boolean but `value` is still the wide Scalar (it comes
      // from the codec, and null means the members disagree), so the hint is
      // asked about a boolean or about nothing.
      <OptionRow
        label={group.label}
        hint={optionBooleanHint(group.prop, typeof group.value === 'boolean' ? group.value : null)}
      >
        <Toggle
          checked={group.value === true}
          onChange={pick}
          label={group.label}
          disabled={disabled || busy || group.value == null}
        />
      </OptionRow>
    );
  }

  let values: Scalar[];
  if (group.type === 'number') {
    const min = group.min ?? 1;
    const max = group.max ?? 6;
    values = [];
    for (let v = min; v <= max; v++) values.push(v);
  } else {
    values = group.allowed || [];
  }
  const options = values.map((v) => ({ value: v, label: optionValueLabel(group.prop, v) }));

  // The panel is ~360px, so a track shares its line with the label only while
  // it stays short. Count the WORDS, not just the segments: two segments
  // reading "Fill the frame / Show the whole image" are wider than eight
  // reading "20% … 75%".
  const many = wideForRow(options);
  return (
    <OptionRow label={group.label} stack={many}>
      <Segmented
        value={group.value}
        onChange={pick}
        options={options}
        disabled={disabled || busy}
        wrap={many}
        label={group.label}
      />
    </OptionRow>
  );
}

/* List groups (ListGroupSection, ItemCard, TableGrid…) live in ListEditor.jsx;
   the shared field/media controls in FieldControls.jsx. */
